import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function SidebarUserMenu({ anchorRef, theme, onClose, children }) {
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [position, setPosition] = useState({ left: 12, top: 12, visibility: 'hidden' });

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft || 0) + 12;
      const topEdge = (viewport?.offsetTop || 0) + 12;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const availableWidth = Math.max(1, width - 24);
      const availableHeight = Math.max(1, height - 24);
      const rail = anchor.closest('.iio-sidebar')?.getBoundingClientRect().width < 100;
      panel.style.width = Math.min(rail ? 240 : rect.width, availableWidth) + 'px';
      panel.style.maxHeight = availableHeight + 'px';
      const menu = panel.getBoundingClientRect();
      const left = Math.max(leftEdge, Math.min(rail ? rect.right + 12 : rect.left, leftEdge + availableWidth - menu.width));
      const top = Math.max(topEdge, Math.min(rect.top - menu.height - 8, topEdge + availableHeight - menu.height));
      setPosition({ left, top, visibility: 'visible' });
    };
    place();
    const outside = event => {
      if (!panel.contains(event.target) && !anchor.contains(event.target)) closeRef.current();
    };
    const keys = event => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        closeRef.current(); anchor.focus({ preventScroll: true });
      }
    };
    const observer = new ResizeObserver(place);
    observer.observe(anchor); observer.observe(panel);
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', keys, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      observer.disconnect();
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', keys, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
      if (panel.contains(document.activeElement)) anchor.focus({ preventScroll: true });
    };
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (position.visibility === 'visible') panelRef.current?.querySelector('button')?.focus({ preventScroll: true });
  }, [position.visibility]);

  const handleKeys = event => {
    const items = [...panelRef.current.querySelectorAll('[role="menuitem"]')];
    const index = items.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;
    if (next !== undefined) { event.preventDefault(); items[next]?.focus(); }
    if (event.key === 'Tab') {
      anchorRef.current?.focus({ preventScroll: true });
      closeRef.current();
    }
  };

  return createPortal(
    <div className="iio ci-user-popover" data-theme={theme} ref={panelRef} style={position} onKeyDown={handleKeys}>
      <div className="iio-sb2-umenu" id="sidebar-user-menu" role="menu" aria-labelledby="sidebar-user-trigger">{children}</div>
    </div>, document.body
  );
}
